import { Link } from 'react-router-dom';
import { Monitor, AlertTriangle, Lock, Unlock, Smartphone } from 'lucide-react';
import { buildCommercantCaissePostesSettingsHref } from '../../utils/posCaisseSettingsNav';
import { formatDeviceDisplayName } from '../../utils/posDeviceDisplay';

/**
 * Bandeau poste financier + session + appareil courant (multi-appareils / même poste).
 */
export default function PosPosteSessionBar({
  poste,
  session,
  multiPostOperational,
  singlePosteMode,
  deviceBlockedCode,
  commercantId,
  activeDeviceCount = null,
  currentDeviceId = null,
  currentDeviceLabel = null,
}) {
  if (deviceBlockedCode === 'DEVICE_NOT_ENROLLED' || deviceBlockedCode === 'POS_DEVICE_REQUIRED') {
    return (
      <div
        data-testid="pos-poste-session-bar"
        data-pos-device-blocked={deviceBlockedCode}
        className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Cet appareil n&apos;est pas rattaché au poste de caisse</p>
          <p className="mt-1 text-amber-800">
            Un poste de caisse = une session financière partagée. Associez cet appareil (PC, iPad…)
            au bon poste dans Paramètres → Caisses.
          </p>
          {commercantId && (
            <Link
              to={buildCommercantCaissePostesSettingsHref(commercantId)}
              className="inline-block mt-2 font-semibold text-amber-900 underline"
            >
              Ouvrir les paramètres caisses
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (!poste && !session && singlePosteMode) return null;

  const posteLabel = poste?.label || poste?.code || 'Caisse principale';
  const sessionOpen = Boolean(session?.id);
  const deviceName = currentDeviceId
    ? formatDeviceDisplayName(currentDeviceId, currentDeviceLabel)
    : null;

  return (
    <div
      data-testid="pos-poste-session-bar"
      data-pos-poste-label={posteLabel}
      data-pos-session-open={sessionOpen ? '1' : '0'}
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border px-4 py-2.5 text-sm ${
        sessionOpen
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-slate-200 bg-slate-50 text-slate-700'
      }`}
    >
      <span className="inline-flex items-center gap-2 font-medium">
        <Monitor className="h-4 w-4 shrink-0" />
        <span>
          Poste : <strong>{posteLabel}</strong>
        </span>
        {multiPostOperational && !singlePosteMode && (
          <span className="text-xs font-normal text-slate-500">(plusieurs postes financiers)</span>
        )}
      </span>
      <span className="inline-flex items-center gap-1.5">
        {sessionOpen ? (
          <>
            <Unlock className="h-4 w-4" />
            Session ouverte
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" />
            Session fermée — ouvrez la caisse pour encaisser
          </>
        )}
      </span>
      {typeof activeDeviceCount === 'number' && activeDeviceCount > 0 ? (
        <span className="text-xs text-slate-600">
          {activeDeviceCount} appareil{activeDeviceCount > 1 ? 's' : ''} sur ce poste
        </span>
      ) : null}
      {deviceName ? (
        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
          <Smartphone className="h-3.5 w-3.5" />
          Appareil actuel : {deviceName}
        </span>
      ) : null}
    </div>
  );
}
